import {
    EDITOR_SAVING,
    fetchFormsForAutocomplete,
    getForms,
    isEmpty,
    toCentsInt,
    toSelectOptions,
    toSimpleCurrencyString,
    toStr,
} from "@rpforms/shared";
import { createCategoryAJAX, createManufacturerAJAX } from "@rpforms/shared/build/actions";
import { PrimaryButton } from "@rpforms/shared/build/components/Button";
import { InputError } from "@rpforms/shared/build/components/layout/Widget";
import { INVENTORY_ITEM_SKILL_OPTIONS } from "@rpforms/shared/build/constants";
import React, { useState, useEffect } from "react";
import { Col, Dropdown, InputGroup, Modal, Row } from "react-bootstrap";
import CurrencyInput from "react-currency-masked-input";
import useForm from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CategorySelect } from "../categories/select";
import { ManufacturerSelect } from "../manufacturers/select";
import { FormFilter } from "../entries/FormFilter";
import { useSearchParam, useStateValidator } from "react-use";
import { FormSelector } from "../forms/FormSelector";
import { RootState } from "../../reducers";
import { SkillSelect } from "../skills/SkillSelect";
import { kebabCase } from "lodash";

interface IModalInventoryItem {
    editor: IEditor;
    next: (payload: any) => {};
}

export const ModalInventoryItem = ({ editor, next }: IModalInventoryItem) => {
    const dispatch = useDispatch();
    const [extraArgs, setExtraArgs] = useState<any>({
        sales_price: 0,
        buying_price: 0,
        ...editor.config.preload,
    });
    const [manufacturer, setManufacturer] = useState({
        value: editor.config.preload.manufacturer_id,
        label: editor.config.preload.manufacturer_name,
    });
    const [category, setCategory] = useState({
        value: editor.config.preload.category_id,
        label: editor.config.preload.category_name,
    });
    const [inventoryItem, setInventoryItem] = useState<any>({ ...editor.config.preload });
    const { register, handleSubmit, errors, watch } = useForm({
        mode: "onChange",
        reValidateMode: "onChange",
    });
    const nameValue = watch("name", inventoryItem.name || "");
    const quantityValue = watch("quantity", inventoryItem.quantity ?? "");

    // Initialize forms state with preloaded form IDs
    const [forms, setForms] = useState<Array<any>>(editor.config.preload.allowed_form_types || []);
    const [selectedFormOptions, setSelectedFormOptions] = useState<Array<any>>([]);

    const allForms = useSelector<RootState, any>((store) => store.forms);

    useEffect(() => {
        dispatch(fetchFormsForAutocomplete(() => {}));
    }, []);

    // Set selected form options when forms are loaded
    useEffect(() => {
        if (allForms.autocomplete && allForms.autocomplete.length > 0 && forms.length > 0) {
            const selectedForms = allForms.autocomplete
                .filter((form: any) => forms.includes(form.id))
                .map((form: any) => ({
                    value: form.id,
                    label: form.name,
                }));
            setSelectedFormOptions(selectedForms);
        }
    }, [allForms.autocomplete, forms]);

    const onSubmit = async (data: any) => {
        const payload = {
            inventory_item: {
                ...editor.config.preload,
                ...extraArgs,
                ...data,
                inventory_type: inventoryItem.inventory_type,
                category_id: category.value,
                manufacturer_id: manufacturer.value,
                form_ids: forms,
            },
        };

        const response = await next(payload);
        if (response) {
            if (editor.config.submitCallback) {
                editor.config.submitCallback(response);
            } else {
                window.location.reload();
            }
        } else {
            toast.error("Ein Fehler ist aufgetreten, bitte prüfen Sie Ihre Eingaben", {
                autoClose: 3000,
            });
            dispatch({ type: EDITOR_SAVING, payload: false });
        }
    };

    const createManufacturer = async (inputValue: any) => {
        const created: any = await dispatch(createManufacturerAJAX({ name: inputValue }));
        setManufacturer({
            value: created.id,
            label: created.name,
        });
    };

    const updateManufacturer = (newValue: any) => {
        setManufacturer(newValue);
    };

    const createCategory = async (inputValue: any) => {
        const created: any = await dispatch(createCategoryAJAX({ name: inputValue }));
        setCategory({
            value: created.id,
            label: created.name,
        });
    };

    const updateCategory = (newValue: any) => {
        setCategory(newValue);
    };

    const updateSalesPrice = (_event: any, maskedvalue: any) => {
        const valueInCents = toCentsInt(maskedvalue);
        setExtraArgs({ ...extraArgs, sales_price: valueInCents });
    };

    const updateBuyingPrice = (_event: any, maskedvalue: any) => {
        const valueInCents = toCentsInt(maskedvalue);
        setExtraArgs({ ...extraArgs, buying_price: valueInCents });
    };

    const updateDescription = (event: { target: { value: any } }) => {
        setExtraArgs({ ...extraArgs, description: event.target.value });
    };

    const updateNr = (event: { target: { value: any } }) => {
        setExtraArgs({ ...extraArgs, nr: event.target.value });
    };

    const updateShortName = (event: { target: { value: any } }) => {
        setExtraArgs({ ...extraArgs, short_name: event.target.value });
    };

    const isQuantifiable = () => {
        return (
            inventoryItem.inventory_type === "repair" || inventoryItem.inventory_type === "disposal"
        );
    };

    const isBuyingPriceInvalid =
        inventoryItem.inventory_type === "disposal" &&
        (!extraArgs.buying_price || extraArgs.buying_price <= 0);
    const isSalesPriceInvalid = !extraArgs.sales_price || extraArgs.sales_price <= 0;

    const isValid =
        (isQuantifiable() &&
            category.value &&
            manufacturer.value &&
            !isSalesPriceInvalid &&
            !isBuyingPriceInvalid) ||
        !isSalesPriceInvalid;
    const isNew = editor.config.new;

    const title = () => {
        let result = "Neue Ware erstellen";
        if (inventoryItem.inventory_type === "repair") {
            result = isNew ? "Neue Ware erstellen" : "Ware bearbeiten";
        } else if (inventoryItem.inventory_type === "service") {
            result = isNew ? "Neue Dienstleistung erstellen" : "Dienstleistung bearbeiten";
        } else if (inventoryItem.inventory_type === "disposal") {
            result = isNew ? "Neue Verkaufspostion erstellen" : "Verkaufspostion bearbeiten";
        }
        return result;
    };

    const inventoryItemTypes = {
        repair: "Reparatur",
        disposal: "Verkauf",
        service: "Service",
    };

    const handleSelect = (selectedOptions) => {
        const hasAllOption = selectedOptions?.some((opt) => opt.value === 0);

        let formIds;
        if (hasAllOption && allForms.autocomplete) {
            formIds = allForms.autocomplete.map((form: any) => form.id);
        } else {
            formIds = selectedOptions
                ? selectedOptions.map((opt) => opt.value).filter((id) => id !== 0)
                : [];
        }
        setForms(formIds);
        setSelectedFormOptions(selectedOptions || []);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Header closeButton>
                <Modal.Title>{title()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <input
                            type="hidden"
                            name="inventory_type"
                            defaultValue={inventoryItem.inventory_type}
                        />
                        <Dropdown>
                            <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                                {inventoryItemTypes[inventoryItem.inventory_type]}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {Object.keys(inventoryItemTypes).map((inventoryType) => {
                                    return (
                                        <Dropdown.Item
                                            onClick={() => {
                                                setInventoryItem({
                                                    ...inventoryItem,
                                                    inventory_type: inventoryType,
                                                });
                                            }}
                                            key={"inventory_type_" + inventoryType}
                                        >
                                            {inventoryItemTypes[inventoryType]}
                                        </Dropdown.Item>
                                    );
                                })}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                </Row>
                <Row>
                    {inventoryItem.inventory_type === "disposal" && (
                        <Col xs={2}>
                            <label>Artikel-Nr</label>
                            <input
                                defaultValue={inventoryItem.nr}
                                className={"form-control"}
                                onChange={updateNr}
                                name="nr"
                            />
                            {errors.nr && <InputError>{errors.nr.message}</InputError>}
                        </Col>
                    )}
                    <Col xs={3}>
                        <label>Protokollansicht</label>
                        <input
                            defaultValue={inventoryItem.short_name}
                            className={"form-control"}
                            onChange={updateShortName}
                            name="short_name"
                        />
                        {!extraArgs.short_name && (
                            <InputError>Bitte Protokollansicht ausfüllen</InputError>
                        )}
                    </Col>
                    {inventoryItem.inventory_type === "disposal" && (
                        <Col>
                            <label>Hersteller</label>
                            <ManufacturerSelect
                                value={manufacturer}
                                onChange={updateManufacturer}
                                onCreateOption={createManufacturer}
                                name="manufacturer_id"
                            />
                            {!manufacturer.value && (
                                <InputError>Bitte Hersteller ausfüllen</InputError>
                            )}
                        </Col>
                    )}
                    {isQuantifiable() && (
                        <Col>
                            <label>Gewerk</label>
                            <CategorySelect
                                value={category}
                                onChange={updateCategory}
                                onCreateOption={createCategory}
                                name="category_id"
                            />
                            {!category.value && <InputError>Bitte Gewerk ausfüllen</InputError>}
                        </Col>
                    )}
                </Row>

                <Row style={{ marginTop: "10px" }}>
                    <Col>
                        <label>Skill</label>
                        <SkillSelect
                            options={INVENTORY_ITEM_SKILL_OPTIONS}
                            value={extraArgs.skills}
                            onChange={(skills) => setExtraArgs({ ...extraArgs, skills })}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <label>Angebotsansicht</label>
                        <input
                            defaultValue={inventoryItem.name}
                            className={"form-control"}
                            name="name"
                            ref={register({
                                required: "Name muss ausgefüllt sein",
                            })}
                        />
                        {(!nameValue || !nameValue.toString().trim()) && (
                            <InputError>
                                {errors.name?.message || "Name muss ausgefüllt sein"}
                            </InputError>
                        )}
                    </Col>
                    {isQuantifiable() && (
                        <Col xs={2}>
                            <label>Anzahl</label>
                            <input
                                type="number"
                                defaultValue={inventoryItem.quantity}
                                className={"form-control"}
                                name="quantity"
                                ref={register({
                                    required: "Anzahl muss ausgefüllt sein",
                                })}
                            />
                            {(quantityValue === "" ||
                                quantityValue === null ||
                                quantityValue === undefined) && (
                                <InputError>
                                    {errors.quantity?.message || "Anzahl muss ausgefüllt sein"}
                                </InputError>
                            )}
                        </Col>
                    )}
                </Row>
                <Row>
                    <Col>
                        <label>Beschreibung</label>
                        <textarea
                            className={"form-control"}
                            name="description"
                            defaultValue={inventoryItem.description}
                            onChange={updateDescription}
                        />
                    </Col>
                </Row>
                <Row>
                    {inventoryItem.inventory_type === "disposal" && (
                        <Col>
                            <label>Einkaufspeis</label>
                            <InputGroup>
                                <CurrencyInput
                                    defaultValue={
                                        inventoryItem.buying_price
                                            ? toSimpleCurrencyString(inventoryItem.buying_price)
                                            : "0.0"
                                    }
                                    className={"form-control"}
                                    onChange={updateBuyingPrice}
                                    name="buying_price"
                                />
                                <InputGroup.Append>
                                    <InputGroup.Text>€</InputGroup.Text>
                                </InputGroup.Append>
                            </InputGroup>
                            {isBuyingPriceInvalid && (
                                <InputError>Einkaufspreis muss größer als 0 sein.</InputError>
                            )}
                        </Col>
                    )}
                    <Col>
                        <label>Verkaufspreis</label>
                        <InputGroup>
                            <CurrencyInput
                                defaultValue={
                                    inventoryItem.sales_price
                                        ? (inventoryItem.sales_price / 100).toString()
                                        : "0.0"
                                }
                                className={"form-control"}
                                onChange={updateSalesPrice}
                                name="sales_price"
                            />
                            <InputGroup.Append>
                                <InputGroup.Text>€</InputGroup.Text>
                            </InputGroup.Append>
                        </InputGroup>
                        {isSalesPriceInvalid && (
                            <InputError>Verkaufspreis muss größer als 0 sein.</InputError>
                        )}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <label>Nutzbar in: </label>
                        <FormSelector
                            includeAllOption
                            isMulti
                            isSearchable
                            onSelect={handleSelect}
                            value={selectedFormOptions}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="form-check mt-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="inventory-item-approved"
                                name="approved"
                                defaultChecked={inventoryItem.approved !== false}
                                ref={register}
                            />
                            <label className="form-check-label" htmlFor="inventory-item-approved">
                                Freigegeben
                            </label>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                {editor.saving && <>speichert …</>}
                <PrimaryButton
                    disabled={!isValid || editor.saving}
                    style={editor.saving ? { background: "gray" } : {}}
                    type={"submit"}
                >
                    {isNew ? "Erstellen" : "Speichern"}
                </PrimaryButton>
            </Modal.Footer>
        </form>
    );
};
